/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface TrmUserProgramProps {
  id: string;
  trmUserId: string;
  programId: string;
  isPreferred: boolean;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class TrmUserProgramRecord extends Record<TrmUserProgramProps>({
  id: null as any,
  trmUserId: null as any,
  programId: null as any,
  isPreferred: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    trmUserId: string;
    programId: string;
    isPreferred: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): TrmUserProgramRecord {
    if (json == null) {
      throw new Error('TrmUserProgramRecord.fromJS: parameter cannot be null');
    }
    return new TrmUserProgramRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'TrmUserProgramJson.id',
      ),
      trmUserId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'trmUserId', '00000000-0000-0000-0000-000000000000'),
        'TrmUserProgramJson.trmUserId',
      ),
      programId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'programId', '00000000-0000-0000-0000-000000000000'),
        'TrmUserProgramJson.programId',
      ),
      isPreferred: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isPreferred', false),
        'TrmUserProgramJson.isPreferred',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'TrmUserProgramJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    trmUserId: string;
    programId: string;
    isPreferred: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [TrmUserProgramRecord, Error[]] {
    if (json == null) {
      throw new Error('TrmUserProgramRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new TrmUserProgramRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'TrmUserProgramJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      trmUserId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'trmUserId', '00000000-0000-0000-0000-000000000000'),
            'TrmUserProgramJson.trmUserId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      programId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'programId', '00000000-0000-0000-0000-000000000000'),
            'TrmUserProgramJson.programId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isPreferred: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isPreferred', false),
            'TrmUserProgramJson.isPreferred',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'TrmUserProgramJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: TrmUserProgramProps) {
    super(props);
    if (props == null) {
      throw new Error('TrmUserProgramRecord constructor parameter cannot be null');
    }
  }
}

export abstract class TrmUserProgramViewModelBase implements TrmUserProgramProps {
  protected readonly _props: TrmUserProgramProps;
  private _json?: TrmUserProgramProps;

  get id(): string {
    return this._props.id;
  }
  get trmUserId(): string {
    return this._props.trmUserId;
  }
  get programId(): string {
    return this._props.programId;
  }
  get isPreferred(): boolean {
    return this._props.isPreferred;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: TrmUserProgramProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        trmUserId: this._props.trmUserId,
        programId: this._props.programId,
        isPreferred: this._props.isPreferred,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllTrmUserProgramEntities = (state: AppState) =>
  state.entityStores.trmUserProgram.entities;

export const selectTrmUserProgramById = (id: string) => (state: AppState) =>
  state.entityStores.trmUserProgram.entities.get(id);

export const selectTrmUserProgramMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.trmUserProgram.meta.get(id);

export const selectTrmUserProgramAndMetadataById = (id: string) =>
  createSelector(
    selectTrmUserProgramById(id),
    selectTrmUserProgramMetadataById(id),
    (trmUserProgram, meta) => ({ trmUserProgram, meta }),
  );

export function selectTrmUserProgramEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllTrmUserProgramEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, TrmUserProgramRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectTrmUserProgramEntitiesByTrmUserId(trmUserId: string) {
  return createSelector(selectAllTrmUserProgramEntities, (entities) =>
    entities.filter((e) => e.trmUserId === trmUserId).valueSeq(),
  );
}

export function selectTrmUserProgramEntitiesByProgramId(programId: string) {
  return createSelector(selectAllTrmUserProgramEntities, (entities) =>
    entities.filter((e) => e.programId === programId).valueSeq(),
  );
}

export function selectTrmUserProgramEntitiesByIsPreferred(isPreferred: boolean) {
  return createSelector(selectAllTrmUserProgramEntities, (entities) =>
    entities.filter((e) => e.isPreferred === isPreferred).valueSeq(),
  );
}

export function selectTrmUserProgramEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllTrmUserProgramEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
